import { useCallback, useEffect, useReducer, useState } from 'react'
import type { Dispatch } from 'react'

const RESET = '@@demo/reset'
type ResetAction = { type: typeof RESET }

type Loaded<S> = { state: S; ok: boolean }

/**
 * A reducer whose state is saved in versioned browser storage under its own key,
 * so each demo domain keeps (and resets) only its own records.
 */
export function useDemoStore<S extends object, A extends { type: string }>(
  storageKey: string,
  reducer: (state: S, action: A) => S,
  createInitial: () => S,
) {
  const [loaded] = useState<Loaded<S>>(() => loadState(storageKey, createInitial))
  const [persistenceOk, setPersistenceOk] = useState(loaded.ok)
  const [state, dispatch] = useReducer(
    (current: S, action: A | ResetAction) => (action.type === RESET ? createInitial() : reducer(current, action as A)),
    loaded.state,
  )

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state))
      setPersistenceOk(true)
    } catch {
      setPersistenceOk(false)
    }
  }, [storageKey, state])

  const reset = useCallback(() => {
    dispatch({ type: RESET })
  }, [])

  return { state, dispatch: dispatch as Dispatch<A>, reset, persistenceOk }
}

function loadState<S extends object>(key: string, createInitial: () => S): Loaded<S> {
  const initial = createInitial()
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(key)
  } catch {
    return { state: initial, ok: false }
  }
  if (!raw) return { state: initial, ok: true }
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') return { state: initial, ok: true }
    const base = initial as Record<string, unknown>
    const merged: Record<string, unknown> = { ...base, ...parsed }
    if (isObject(base.ui) && isObject(parsed.ui)) merged.ui = { ...base.ui, ...parsed.ui }
    return { state: merged as S, ok: true }
  } catch {
    return { state: initial, ok: true }
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export const nowIso = () => new Date().toISOString()

export const formatId = (prefix: string, n: number) => `${prefix}-${String(n).padStart(3, '0')}`

const timestampFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

export function formatTimestamp(iso: string) {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : timestampFormat.format(date)
}

export function formatDate(iso: string) {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : dateFormat.format(date)
}

export function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
